import type { EntityPublic } from '@api/endpoints/workspaces/entity-types/post-workspace-entity-type-search-entities-schema'
import type { Stylable } from '@core/utils/types'
import { EmptyScreen } from '@design/components/screens/empty-screen/EmptyScreen'
import { NavigationTabs } from '@design/components/tabs/navigation-tabs/NavigationTabs'
import { EntityOverviewTab } from '@discover/features/entities/components/EntityOverviewTab'
import { cn } from 'cn'
import type { LucideIcon } from 'lucide-react'
import {
  BarChart3,
  Building2,
  ClipboardList,
  LayoutGrid,
  Sparkles,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

export const entitiesTabs = ['overview', 'stats', 'claims'] as const
export type EntitiesTabId = (typeof entitiesTabs)[number]

const tabIcons: Record<EntitiesTabId, LucideIcon> = {
  overview: LayoutGrid,
  stats: BarChart3,
  claims: ClipboardList,
}

type EntitiesTabsProps = Stylable & {
  workspaceId: string
  propertyDfnId: string
  entity: EntityPublic | null
  activeTab: EntitiesTabId
}

export function EntitiesTabs({
  workspaceId,
  propertyDfnId,
  entity,
  activeTab,
  className,
}: EntitiesTabsProps) {
  const { t } = useTranslation()

  if (!entity) {
    return (
      <EmptyScreen
        className={className}
        icon={<Building2 />}
        title={t('entities.empty.title')}
        description={t('entities.empty.description')}
      />
    )
  }

  const items = entitiesTabs.map((tab) => ({
    value: tab,
    label: t(`entities.tabs.${tab}`),
    icon: tabIcons[tab],
    url: '.',
    search: (prev: Record<string, unknown>) => ({ ...prev, tab }),
  }))

  return (
    <div
      className={cn(
        'flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-auto p-6',
        className
      )}
    >
      <h2 className='font-semibold text-foreground text-lg'>{entity.name}</h2>
      <NavigationTabs activeTab={activeTab} items={items} />
      <TabContent
        activeTab={activeTab}
        workspaceId={workspaceId}
        propertyDfnId={propertyDfnId}
        standardId={entity.standardId}
      />
    </div>
  )
}

interface TabContentProps {
  activeTab: EntitiesTabId
  workspaceId: string
  propertyDfnId: string
  standardId: string
}

function TabContent({
  activeTab,
  workspaceId,
  propertyDfnId,
  standardId,
}: TabContentProps) {
  const { t } = useTranslation()

  if (activeTab === 'overview') {
    return (
      <EntityOverviewTab
        workspaceId={workspaceId}
        propertyDfnId={propertyDfnId}
        standardId={standardId}
      />
    )
  }

  return (
    <EmptyScreen
      className='flex-1'
      icon={<Sparkles />}
      title={t('entities.comingSoon.title')}
      description={t('entities.comingSoon.description')}
    />
  )
}
